// Styles
import "./QAResultsNew.scss";

// React Hooks
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// UI Components
import {
  Headline3,
  Subtitle2,
  ButtonIcon,
  Icon,
  ButtonRound,
} from "@bms-ai/bms-ai-ui-lib/components";

// Components
import { StoryboardSection } from "./components/StoryboardSection";
import { ManuscriptSection } from "./components/ManuscriptSection";
import { FigmaSection } from "./components/FigmaSection";

// Context
import NewStateIcon from "../../_assets/icons/NewStateIcon";
import SubmittedIcon from "../../_assets/icons/SubmittedIcon";
import RoutedIcon from "../../_assets/icons/RoutedIcon";
import PlanningIcon from "../../_assets/icons/PlanningIcon";
import RunningIcon from "../../_assets/icons/RunningIcon";
import CompleteIcon from "../../_assets/icons/CompleteIcon";
import CheckmarkIcon from "../../_assets/icons/CheckmarkIcon";
import WarningIcon from "../../_assets/icons/WarningIcon";
import TaskIdIcon from "../../_assets/icons/TaskIdIcon";
import BrandIcon from "../../_assets/icons/BrandIcon";
import ChannelIcon from "../../_assets/icons/ChannelIcon";
import DateIcon from "../../_assets/icons/DateIcon";

// Context
import { useQATask } from "../../context/QATaskContext";
import { useMultipleFileUpload } from "../../context/MultipleFileUploadContext";

// API
import {
  getTaskStatus,
  getTaskResults,
} from "../../api/data-services/task-api";
import { downloadFrameImage } from "../../api/data-services/file-upload";
import {
  downloadCSVReport,
  downloadPDFReport,
} from "../../api/data-services/results-api";
import { useSession } from "../../context/session-context";
import FailIcon from "../../_assets/icons/FailIcon";
import ErrorIcon from "../../_assets/icons/ErrorIcon";

// Types
export interface Finding {
  finding_id: string;
  check_id: string;
  category: string;
  result: "PASS" | "FAIL" | "BRAND_GUIDELINE_NOT_AVAILABLE" | "NA";
  severity: "INFO" | "MINOR" | "MAJOR" | "CRITICAL";
  message: string;
  frame_id: string | null;
  doc_id: string | null;
  source_type: string | null;
  actual: string | null;
  expected: string | null;
  recommendation: string | null;
  used_llm: boolean;
  confidence: number | null;
  location?: string | null;
}

export interface Frame {
  frame_id: string;
  doc_id: string;
  frame_number: number;
  source_type: string | null;
}

export interface TaskResults {
  task_id: string;
  outputs_json: {
    brand: string;
    status: "PASS" | "FAIL";
    channel: string;
    task_id: string;
    findings: Finding[];
    analysis_date: string;
    compliance_score: number;
    frames?: Frame[];
    total_checks?: number;
    issues_found?: number;
    frames_detected?: number;
    storyboard_status?: "PASS" | "FAIL";
    is_last_consignment?: boolean;
  };
}

interface TaskStatusResponse {
  task_id: string;
  status:
    | "new"
    | "submitted"
    | "routed"
    | "planned"
    | "running"
    | "complete"
    | "failed";
}

// Helper function to format messages with line breaks
export const formatMessage = (message: string) => {
  return message.split("\n").map((line, index, array) => (
    <>
      {line}
      {index < array.length - 1 && <br />}
    </>
  ));
};

const QAResultsNew: React.FC = () => {
  const navigate = useNavigate();
  const {
    taskState,
    setStatus,
    setResults,
    setError,
    resetTask,
    taskResults,
    setTaskResults,
    taskCreated,
  } = useQATask();
  const { sessionDetails, moduleAccessToken } = useSession();
  const { figmaDetails } = useMultipleFileUpload();
  const [currentStatus, setCurrentStatus] = useState<string>("new");
  const [isPolling, setIsPolling] = useState<boolean>(true);
  const [pollingError, setPollingError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState<number>(0);
  const [frameImages, setFrameImages] = useState<Map<string, string>>(
    new Map(),
  );
  const [frameSasUrls, setFrameSasUrls] = useState<Map<string, string>>(
    new Map(),
  );
  // const [loadingImages, setLoadingImages] = useState<boolean>(false);
  const [expandedFindings, setExpandedFindings] = useState<Set<string>>(
    new Set(),
  );
  const [expandedFrames, setExpandedFrames] = useState<Set<string>>(new Set());
  const [filesExpanded, setFilesExpanded] = useState<boolean>(false);
  const [expandedSection, setExpandedSection] = useState<
    "storyboard" | "manuscript" | "figma" | null
  >(null);
  const { uploadedFiles } = useMultipleFileUpload();
  const [showTooltip, setShowTooltip] = useState<boolean>(false);

  // Filter state for summary cards
  const [activeFilter, setActiveFilter] = useState<"all" | "passed" | "failed">(
    "all",
  );

  // Categorized findings states
  const [frameFindings, setFrameFindings] = useState<Finding[]>([]);
  const [figmaFrameFindings, setFigmaFrameFindings] = useState<Finding[]>([]);
  const [figmaFindings, setFigmaFindings] = useState<Finding[]>([]);
  const [manuscriptFindings, setManuscriptFindings] = useState<Finding[]>([]);
  const [storyboardFindings, setStoryboardFindings] = useState<Finding[]>([]);
  const [otherStoryboardFindings, setOtherStoryboardFindings] = useState<
    Finding[]
  >([]);

  // Download states
  const [downloadingPDF, setDownloadingPDF] = useState<boolean>(false);
  const [downloadingCSV, setDownloadingCSV] = useState<boolean>(false);

  // Results polling states
  const [isPollingResults, setIsPollingResults] = useState<boolean>(false);
  const [_resultsPollingError, setResultsPollingError] = useState<
    string | null
  >(null);

  // Track downloaded docIds to avoid re-fetching images on polling cycles
  const [downloadedDocIds, setDownloadedDocIds] = useState<Set<string>>(
    new Set(),
  );

  // Track mount status to prevent URL revocation during streaming updates
  const isMountedRef = useRef(true);

  // Process findings based on channel when results are available
  useEffect(() => {
    if (!taskResults?.outputs_json?.findings) {
      // Clear all categorized findings if no results
      setFrameFindings([]);
      setFigmaFrameFindings([]);
      setFigmaFindings([]);
      setManuscriptFindings([]);
      setStoryboardFindings([]);
      setOtherStoryboardFindings([]);
      return;
    }

    const findings = taskResults.outputs_json.findings;
    const channel = taskResults.outputs_json.channel?.toLowerCase();
    const isBanner = channel === "banner" || channel === "animated_banner";

    const frameFindings: Finding[] = [];
    const figmaFindings: Finding[] = [];
    const figmaFrameFindings: Finding[] = [];
    const manuscriptFindings: Finding[] = [];
    const storyboardFindings: Finding[] = [];
    const otherStoryboardFindings: Finding[] = [];

    findings.forEach((finding) => {
      if (isBanner) {
        // Banner channel logic
        if (finding.check_id?.toLowerCase() === "grammar_spelling_check") {
          manuscriptFindings.push(finding);
        } else if (
          finding.check_id?.toLowerCase() === "banner_size_check" ||
          finding.check_id?.toLowerCase() === "url_check" ||
          finding.check_id?.toLowerCase() === "animation_notes_match" ||
          finding.check_id?.toLowerCase() === "isi_copy_vs_manuscript"
        ) {
          otherStoryboardFindings.push(finding);
        } else if (
          finding.check_id?.toLowerCase().startsWith("figma_") ||
          finding.source_type === "figma_frame"
        ) {
          figmaFrameFindings.push(finding);
        } else {
          frameFindings.push(finding);
        }
      } else {
        // Non-banner channel logic
        if (finding.check_id?.toLowerCase() === "grammar_spelling_check") {
          manuscriptFindings.push(finding);
        } else if (
          finding.check_id?.toLowerCase().startsWith("figma_") ||
          finding.source_type === "figma_frame"
        ) {
          figmaFindings.push(finding);
        } else {
          storyboardFindings.push(finding);
        }
      }
    });

    setFrameFindings(frameFindings);
    setFigmaFrameFindings(figmaFrameFindings);
    setFigmaFindings(figmaFindings);
    setManuscriptFindings(manuscriptFindings);
    setStoryboardFindings(storyboardFindings);
    setOtherStoryboardFindings(otherStoryboardFindings);
  }, [taskResults]);

  // Initialize taskResults from context if already available
  useEffect(() => {
    if (taskState.results && !taskResults) {
      setTaskResults(taskState.results);
      setIsPolling(false);
    }
  }, [taskState.results, taskResults]);

  // Poll task status every 30 seconds
  useEffect(() => {
    if (!taskState.taskId) {
      console.error("No task ID found");
      setError("No task ID found. Please start from file upload.");
      setIsPolling(false);
      return;
    }

    // Don't start polling if we already have results
    if (taskResults) {
      return;
    }

    // Don't start polling if there's an error
    if (pollingError) {
      console.log("Polling error exists, skipping polling");
      return;
    }

    let intervalId: number | null = null;
    let isActive = true;
    const startTime = Date.now();
    const TIMEOUT_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds

    const pollTaskStatus = async () => {
      if (!isActive) return;

      // Check if 30 minutes have passed
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime >= TIMEOUT_DURATION) {
        console.log("Polling timeout after 30 minutes");
        setIsPolling(false);
        setPollingError("This request has timed out.");

        // Stop polling
        if (intervalId !== null) {
          clearInterval(intervalId);
          intervalId = null;
        }
        return;
      }

      try {
        const statusResponse: TaskStatusResponse = await getTaskStatus(
          taskState.taskId!,
          moduleAccessToken,
        );

        if (!isActive) return;

        setCurrentStatus(statusResponse.status);

        // Check if task is running
        if (
          statusResponse.status === "running" ||
          statusResponse.status === "complete"
        ) {
          // Task is running, set status to running first (show in UI)
          // console.log("Task status is running, showing running stage...");
          // setStatus("");

          // Stop status polling
          if (intervalId !== null) {
            clearInterval(intervalId);
            intervalId = null;
          }

          // Delay transition to completed so user can see "Running" stage
          setTimeout(() => {
            if (isActive) {
              console.log(
                "Transitioning to completed and starting results polling...",
              );
              setStatus("completed");
              setResultsPollingError(null);
              setIsPolling(false);
              setIsPollingResults(true);
              // Clear the hard timeout since we're transitioning to results polling
              clearTimeout(timeoutId);
            }
          }, 2000); // 2 second delay
        } else if (statusResponse.status === "failed") {
          // Task failed
          console.error("Task failed");
          setStatus("failed");
          setPollingError("Task processing failed.");
          setIsPolling(false);

          // Stop polling
          if (intervalId !== null) {
            clearInterval(intervalId);
            intervalId = null;
          }
        } else {
          // Task still processing
          setStatus("processing");
        }
      } catch (error) {
        console.error("Error polling task status:", error);
        if (!isActive) return;

        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to fetch task status";
        setPollingError(errorMessage);
        setIsPolling(false);

        // Stop polling on error
        if (intervalId !== null) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }
    };

    // Start polling immediately
    pollTaskStatus();

    // Poll every 30 seconds
    intervalId = setInterval(pollTaskStatus, 30000);

    // Set a hard timeout to stop polling after 30 minutes
    const timeoutId = setTimeout(() => {
      console.log("Hard timeout reached - stopping all polling");
      isActive = false;
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
      setIsPolling(false);
      setPollingError("This request has timed out.");
    }, TIMEOUT_DURATION);

    // Cleanup on unmount
    return () => {
      isActive = false;
      if (intervalId !== null) {
        clearInterval(intervalId);
      }
      clearTimeout(timeoutId);
    };
  }, [taskState.taskId, taskResults, pollingError, retryKey]);

  // Poll results API every 30 seconds after status is complete
  useEffect(() => {
    if (!isPollingResults || !taskState.taskId) {
      return;
    }

    let resultsIntervalId: number | null = null;
    let isActive = true;
    const resultsStartTime = Date.now();
    const RESULTS_TIMEOUT_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds

    const pollTaskResults = async () => {
      if (!isActive) return;

      // Check if 30 minutes have passed
      const elapsedTime = Date.now() - resultsStartTime;
      if (elapsedTime >= RESULTS_TIMEOUT_DURATION) {
        console.log("Results polling timeout after 30 minutes");
        setIsPollingResults(false);
        setResultsPollingError("Results fetching timed out after 30 minutes.");

        // Stop results polling
        if (resultsIntervalId !== null) {
          clearInterval(resultsIntervalId);
          resultsIntervalId = null;
        }
        return;
      }

      try {
        const resultsResponse: TaskResults = await getTaskResults(
          taskState.taskId!,
          moduleAccessToken,
        );
        // const resultsResponse: TaskResults = await mockGetTaskResults(
        //   taskState.taskId!,
        //   moduleAccessToken,
        // );
        if (!isActive) return;

        // Check if response has error (API failure response)
        if (
          "success" in (resultsResponse as any) &&
          (resultsResponse as any).success === false
        ) {
          console.error("No results received from API");
          return;
        }
        // Check if outputs_json is null (results not ready yet)
        if (resultsResponse?.outputs_json === null) {
          console.log(
            "Results not ready yet (outputs_json is null), continuing to poll...",
          );
          return;
        }

        // Replace taskResults with new response
        setTaskResults(resultsResponse);
        setResults(resultsResponse);
        console.log(
          "Results chunk received. Is last consignment:",
          resultsResponse?.outputs_json?.is_last_consignment,
        );

        // Check if this is the last consignment
        if (resultsResponse?.outputs_json?.is_last_consignment) {
          console.log("All results received (is_last_consignment: true)");
          setIsPollingResults(false);

          // Stop results polling
          if (resultsIntervalId !== null) {
            clearInterval(resultsIntervalId);
            resultsIntervalId = null;
          }
        }
      } catch (error) {
        console.error("Error polling task results:", error);
        if (!isActive) return;

        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to fetch task results";
        setResultsPollingError(errorMessage);
        setIsPollingResults(false);

        // Stop results polling on error
        if (resultsIntervalId !== null) {
          clearInterval(resultsIntervalId);
          resultsIntervalId = null;
        }
      }
    };

    // Call pollTaskResults immediately, then poll every 15 seconds
    pollTaskResults();
    resultsIntervalId = setInterval(pollTaskResults, 15000);

    // Set a hard timeout to stop polling after 10 minutes
    const resultsTimeoutId = setTimeout(() => {
      console.log("Hard timeout reached for results polling");
      isActive = false;
      if (resultsIntervalId !== null) {
        clearInterval(resultsIntervalId);
        resultsIntervalId = null;
      }
      setIsPollingResults(false);
      setResultsPollingError("Results fetching timed out after 10 minutes.");
    }, RESULTS_TIMEOUT_DURATION);

    // Cleanup on unmount
    return () => {
      isActive = false;
      if (resultsIntervalId !== null) {
        clearInterval(resultsIntervalId);
      }
      clearTimeout(resultsTimeoutId);
    };
  }, [isPollingResults, taskState.taskId, moduleAccessToken]);

  // Fetch frame images when frameFindings or figmaFrameFindings are available
  useEffect(() => {
    // Only fetch if we have frameFindings or figmaFrameFindings
    if (frameFindings.length === 0 && figmaFrameFindings.length === 0) {
      return;
    }

    const fetchFrameImages = async () => {
      // setLoadingImages(true);
      const imageMap = new Map<string, string>();
      const newDocIds: string[] = [];

      try {
        // Extract unique doc_ids from frameFindings and figmaFrameFindings only
        const uniqueDocIds = new Map<string, string>();

        // Add frames from frameFindings
        frameFindings.forEach((finding) => {
          if (finding.doc_id && finding.frame_id) {
            uniqueDocIds.set(finding.frame_id, finding.doc_id);
          }
        });

        // Add frames from figmaFrameFindings
        figmaFrameFindings.forEach((finding) => {
          if (finding.doc_id && finding.frame_id) {
            uniqueDocIds.set(finding.frame_id, finding.doc_id);
          }
        });

        // Fetch only new frame images that haven't been downloaded yet
        const token = sessionDetails.accessToken;
        const imagePromises = Array.from(uniqueDocIds.entries())
          .filter(([, docId]) => !downloadedDocIds.has(docId)) // Only fetch if docId not already downloaded
          .map(async ([frameId, docId]) => {
            const imageUrl = await downloadFrameImage(
              docId,
              sessionDetails.client_id || "",
              token,
            );
            if (imageUrl) {
              imageMap.set(frameId, imageUrl);
              newDocIds.push(docId);
            } else {
              console.error(`Failed to load image for frame ${frameId}`);
            }
            return { frameId, imageUrl };
          });

        await Promise.all(imagePromises);

        // Merge new images with existing frameImages
        if (imageMap.size > 0) {
          setFrameImages((prevImages) => new Map([...prevImages, ...imageMap]));
          // Add new docIds to downloaded set
          setDownloadedDocIds((prevSet) => new Set([...prevSet, ...newDocIds]));
        }
      } catch (error) {
        console.error("Error fetching frame images:", error);
      } finally {
        // setLoadingImages(false);
      }
    };

    fetchFrameImages();

    // Only revoke URLs on actual component unmount, not on streaming updates
    // This prevents URLs from being cleared when new findings arrive
    return () => {
      // No-op: cleanup will happen in the unmount useEffect below
    };
  }, [
    frameFindings,
    figmaFrameFindings,
    sessionDetails.accessToken,
    downloadedDocIds,
  ]);

  // Cleanup URLs only when component unmounts
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      frameImages.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);
  // Fetch SAS URLs for Email HoE blur_pixelation_check frames
  useEffect(() => {
    if (
      !taskResults?.outputs_json?.findings ||
      taskResults.outputs_json.findings.length === 0 ||
      (taskResults.outputs_json.channel !== "hoe" &&
        taskResults.outputs_json.channel !== "fte")
    ) {
      return;
    }

    const fetchSasUrls = async () => {
      const sasUrlMap = new Map<string, string>();

      try {
        // Find frames with blur_pixelation_check or image_cropping_check in Email HoE storyboard_frame
        const blurPixelationFrames = new Map<string, string>();
        taskResults.outputs_json.findings.forEach((finding) => {
          if (
            finding.doc_id &&
            finding.frame_id &&
            finding.source_type === "storyboard_frame" &&
            (finding.check_id?.toLowerCase() === "blur_pixelation_check" ||
              finding.check_id?.toLowerCase() === "image_cropping_check")
          ) {
            blurPixelationFrames.set(finding.frame_id, finding.doc_id);
          }
        });

        console.log(
          "Blur pixelation frames to fetch SAS URLs for:",
          blurPixelationFrames.size,
        );

        // Fetch all frame images
        const token = sessionDetails.accessToken;
        const sasUrlPromises = Array.from(blurPixelationFrames.entries()).map(
          async ([frameId, docId]) => {
            const imageUrl = await downloadFrameImage(
              docId,
              sessionDetails.client_id || "",
              token,
              true,
            );
            if (imageUrl) {
              sasUrlMap.set(frameId, imageUrl);
            } else {
              console.error(`Failed to fetch image URL for frame ${frameId}`);
            }
            return { frameId, imageUrl };
          },
        );

        await Promise.all(sasUrlPromises);
        setFrameSasUrls(sasUrlMap);
      } catch (error) {
        console.error("Error fetching SAS URLs:", error);
      }
    };

    fetchSasUrls();
  }, [taskResults, sessionDetails.accessToken]);

  const clearTaskState = () => {
    // Clear context state
    resetTask();

    // Clear local component state
    setTaskResults(null);
    setCurrentStatus("new");
    setIsPolling(false);
    setPollingError(null);
    setIsPollingResults(false);
    setResultsPollingError(null);
    setFrameImages(new Map());
    setFrameSasUrls(new Map());
    setExpandedFindings(new Set());
    setExpandedFrames(new Set());
    setActiveFilter("all");
    setExpandedSection(null);
    setDownloadedDocIds(new Set());
  };

  const handleBack = () => {
    clearTaskState();
    navigate("/file-uploads");
  };

  const handleClose = () => {
    clearTaskState();
    navigate("/file-uploads");
  };

  const handleRetry = async () => {
    console.log("Retrying by restarting polling...");

    // Validate that we have task ID
    if (!taskState.taskId) {
      console.error("No task ID available");
      setError(
        "Unable to retry: No task ID available. Please go back and start from file uploads.",
      );
      return;
    }

    // Clear previous errors and reset states
    setPollingError(null);
    setResultsPollingError(null);
    setError("");

    // Reset local states and restart polling
    setTaskResults(null);
    setIsPolling(true);
    setIsPollingResults(false);

    // Immediately call status API instead of waiting for polling interval
    try {
      const statusResponse: TaskStatusResponse = await getTaskStatus(
        taskState.taskId,
        moduleAccessToken,
      );

      setCurrentStatus(statusResponse.status);

      // Check if task is already complete
      if (statusResponse.status === "complete") {
        console.log("Task already completed, starting results polling...");
        setStatus("completed");
        setIsPolling(false);
        setIsPollingResults(true);
      } else if (statusResponse.status === "failed") {
        console.error("Task failed");
        setStatus("failed");
        setPollingError("Task processing failed.");
        setIsPolling(false);
      } else {
        // Task still processing, increment retry key to restart polling
        setStatus("processing");
        setRetryKey((prev) => prev + 1);
      }
    } catch (error) {
      console.error("Error checking task status on retry:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch task status";
      setPollingError(errorMessage);
      setIsPolling(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!taskState.taskId) {
      console.error("No task ID available for download");
      return;
    }

    setDownloadingPDF(true);
    try {
      await downloadPDFReport(
        taskState.taskId,
        // sessionDetails.client_id || "",
        moduleAccessToken,
      );
      console.log("PDF download completed");
    } catch (error) {
      console.error("Failed to download PDF:", error);
      alert(
        error instanceof Error
          ? error.message
          : "Failed to download PDF report. Please try again.",
      );
    } finally {
      setDownloadingPDF(false);
    }
  };

  const handleDownloadCSV = async () => {
    if (!taskState.taskId) {
      console.error("No task ID available for download");
      return;
    }

    setDownloadingCSV(true);
    try {
      await downloadCSVReport(
        taskState.taskId,
        // sessionDetails.client_id || "",
        moduleAccessToken,
      );
      console.log("CSV download completed");
    } catch (error) {
      console.error("Failed to download CSV:", error);
      alert(
        error instanceof Error
          ? error.message
          : "Failed to download CSV report. Please try again.",
      );
    } finally {
      setDownloadingCSV(false);
    }
  };

  const handleGoToDashboard = () => {
    clearTaskState();
    navigate("/dashboard");
  };

  const getStatusStage = (status: string): number => {
    const stageMap: { [key: string]: number } = {
      new: 0,
      submitted: 1,
      routed: 2,
      planned: 3,
      running: 4,
      complete: 5,
    };
    return stageMap[status] || 0;
  };

  const formatChannelName = (channel: string): string => {
    const channelMap: { [key: string]: string } = {
      banner: "Static Banner",
      fte: "Email FTE",
      hoe: "Email HOE",
      // rte: "Email RTE",
      animated_banner: "Animated Banner",
    };
    return channelMap[channel?.toLowerCase()] || channel;
  };

  const progressStages = [
    { key: "new", label: "New" },
    { key: "submitted", label: "Submitted" },
    { key: "routed", label: "Routed" },
    { key: "planned", label: "Planning" },
    { key: "running", label: "Running" },
    { key: "complete", label: "Complete" },
  ];

  const failedFindings =
    taskResults?.outputs_json.findings.filter((f) => f.result === "FAIL") || [];
  const passedFindings =
    taskResults?.outputs_json.findings.filter((f) => f.result === "PASS") || [];
  const otherFindings =
    taskResults?.outputs_json.findings.filter(
      (f) => f.result !== "PASS" && f.result !== "FAIL",
    ) || [];

  // // Extract unique frames from findings
  // const uniqueFrames = taskResults?.outputs_json.findings.reduce(
  //   (acc, finding, index) => {
  //     if (finding.frame_id && finding.doc_id && !acc.has(finding.frame_id)) {
  //       // Try to extract numeric frame number from frame_id
  //       const frameMatch = finding.frame_id.match(/_frame_(\d+)$/);
  //       const frameNumber = frameMatch
  //         ? parseInt(frameMatch[1], 10)
  //         : index + 1;

  //       acc.set(finding.frame_id, {
  //         frame_id: finding.frame_id,
  //         doc_id: finding.doc_id,
  //         frame_number: frameNumber,
  //         source_type: finding.source_type,
  //       });
  //     }
  //     return acc;
  //   },
  //   new Map<string, Frame>(),
  // );

  // const frames = uniqueFrames
  //   ? Array.from(uniqueFrames.values()).sort((a, b) => {
  //       // First sort by source_type
  //       const sourceTypeOrder: { [key: string]: number } = {
  //         storyboard_frame: 1,
  //         manuscript: 2,
  //         brief: 3,
  //         figma: 4,
  //         unknown: 5,
  //       };
  //       const aOrder = sourceTypeOrder[a.source_type || "unknown"] || 99;
  //       const bOrder = sourceTypeOrder[b.source_type || "unknown"] || 99;

  //       if (aOrder !== bOrder) {
  //         return aOrder - bOrder;
  //       }

  //       // Then sort by frame_number within same source_type
  //       return a.frame_number - b.frame_number;
  //     })
  //   : [];

  // Compute summary stats
  const totalChecks =
    taskResults?.outputs_json?.total_checks ??
    taskResults?.outputs_json.findings.length ??
    0;
  // const framesDetected =
  //   taskResults?.outputs_json?.frames_detected ?? frames.length ?? 0;

  // Check if results are complete (last consignment received)
  const isResultsComplete =
    taskResults?.outputs_json?.is_last_consignment === true;

  // Filter findings based on active filter
  const filterFindings = (findings: Finding[]) => {
    if (activeFilter === "all") return findings;
    return findings.filter((f) =>
      activeFilter === "passed" ? f.result === "PASS" : f.result === "FAIL",
    );
  };

  const filteredFrameFindings = filterFindings(frameFindings);
  const filteredStoryboardFindings = filterFindings(storyboardFindings);
  const filteredManuscriptFindings = filterFindings(manuscriptFindings);
  const filteredFigmaFrameFindings = filterFindings(figmaFrameFindings);
  const filteredFigmaFindings = filterFindings(figmaFindings);
  const filteredOtherStoryboardFindings = filterFindings(
    otherStoryboardFindings,
  );

  const handleSectionToggle = (
    section: "storyboard" | "manuscript" | "figma",
  ) => {
    const isExpanding = expandedSection !== section;

    if (isExpanding) {
      setExpandedSection(section);

      // Auto-select first finding and frame/check in the section being expanded
      const firstSections = new Set<string>();
      const firstFindings = new Set<string>();

      if (section === "storyboard") {
        const filteredFrame = filterFindings(frameFindings);
        const filteredStoryboard = filterFindings(storyboardFindings);
        const filteredOtherStoryboard = filterFindings(otherStoryboardFindings);

        if (filteredFrame.length > 0 && filteredFrame[0].frame_id) {
          firstSections.add(filteredFrame[0].frame_id);
          firstFindings.add(filteredFrame[0].finding_id);
        } else if (filteredStoryboard.length > 0) {
          const firstCheckId = filteredStoryboard[0].check_id;
          if (
            (firstCheckId?.toLowerCase() === "blur_pixelation_check" ||
              firstCheckId?.toLowerCase() === "image_cropping_check") &&
            filteredStoryboard[0].frame_id
          ) {
            firstSections.add(filteredStoryboard[0].frame_id);
          } else {
            firstSections.add(`check_${firstCheckId}`);
          }
          firstFindings.add(filteredStoryboard[0].finding_id);
        } else if (filteredOtherStoryboard.length > 0) {
          firstSections.add(`check_${filteredOtherStoryboard[0].check_id}`);
          firstFindings.add(filteredOtherStoryboard[0].finding_id);
        }
      } else if (section === "manuscript") {
        const filteredManuscript = filterFindings(manuscriptFindings);
        if (filteredManuscript.length > 0) {
          firstSections.add(`check_${filteredManuscript[0].check_id}`);
          firstFindings.add(filteredManuscript[0].finding_id);
        }
      } else if (section === "figma") {
        const filteredFigmaFrame = filterFindings(figmaFrameFindings);
        const filteredFigma = filterFindings(figmaFindings);

        if (filteredFigmaFrame.length > 0 && filteredFigmaFrame[0].frame_id) {
          firstSections.add(filteredFigmaFrame[0].frame_id);
          firstFindings.add(filteredFigmaFrame[0].finding_id);
        } else if (filteredFigma.length > 0) {
          firstSections.add(`check_${filteredFigma[0].check_id}`);
          firstFindings.add(filteredFigma[0].finding_id);
        }
      }

      setExpandedFrames(firstSections);
      setExpandedFindings(firstFindings);
    } else {
      setExpandedSection(null);
    }
  };

  // Expand first frame-section and accordion in each category on initial load
  useEffect(() => {
    if (
      taskResults?.outputs_json.findings &&
      expandedFindings.size === 0 &&
      expandedFrames.size === 0
    ) {
      // Open first frame-section in each category
      const firstSections = new Set<string>();

      // Manuscript: First check_id
      if (manuscriptFindings.length > 0) {
        firstSections.add(`check_${manuscriptFindings[0].check_id}`);
      }

      // Storyboard (banner channel): First frame_id
      if (frameFindings.length > 0 && frameFindings[0].frame_id) {
        firstSections.add(frameFindings[0].frame_id);
      }

      // Storyboard (non-banner channel): First check's key
      if (storyboardFindings.length > 0) {
        const firstCheckId = storyboardFindings[0].check_id;
        // Blur or image cropping check uses frame_id
        if (
          (firstCheckId?.toLowerCase() === "blur_pixelation_check" ||
            firstCheckId?.toLowerCase() === "image_cropping_check") &&
          storyboardFindings[0].frame_id
        ) {
          firstSections.add(storyboardFindings[0].frame_id);
        } else {
          firstSections.add(`check_${firstCheckId}`);
        }
      }

      // Figma (Banner): First frame_id
      if (figmaFrameFindings.length > 0 && figmaFrameFindings[0].frame_id) {
        firstSections.add(figmaFrameFindings[0].frame_id);
      }

      // Figma (Non-Banner): First check_id
      if (figmaFindings.length > 0) {
        firstSections.add(`check_${figmaFindings[0].check_id}`);
      }

      setExpandedFrames(firstSections);

      // Auto-expand first finding in each opened section - Priority: Figma > Storyboard > Manuscript
      const firstFindings = new Set<string>();
      // Figma findings first
      if (figmaFrameFindings.length > 0) {
        firstFindings.add(figmaFrameFindings[0].finding_id);
      }
      if (figmaFindings.length > 0) {
        firstFindings.add(figmaFindings[0].finding_id);
      }
      // Then Storyboard findings
      if (frameFindings.length > 0) {
        firstFindings.add(frameFindings[0].finding_id);
      }
      if (storyboardFindings.length > 0) {
        firstFindings.add(storyboardFindings[0].finding_id);
      }
      // Then Manuscript findings
      if (manuscriptFindings.length > 0) {
        firstFindings.add(manuscriptFindings[0].finding_id);
      }

      setExpandedFindings(firstFindings);

      // Determine which section to expand first - Priority: Figma > Storyboard > Manuscript
      if (figmaFrameFindings.length > 0 || figmaFindings.length > 0) {
        setExpandedSection("figma");
      } else if (
        frameFindings.length > 0 ||
        storyboardFindings.length > 0 ||
        otherStoryboardFindings.length > 0
      ) {
        setExpandedSection("storyboard");
      } else if (manuscriptFindings.length > 0) {
        setExpandedSection("manuscript");
      }
    }
  }, [
    taskResults,
    frameFindings,
    storyboardFindings,
    manuscriptFindings,
    figmaFrameFindings,
    figmaFindings,
    otherStoryboardFindings,
    expandedFindings.size,
    expandedFrames.size,
  ]);

  // // Calculate progress percent for progress bar
  // const progressPercent = (() => {
  //   // Find the current stage index
  //   const currentStageIndex = getStatusStage(currentStatus);
  //   // Number of stages (avoid division by zero)
  //   const totalStages = progressStages.length - 1;
  //   if (totalStages <= 0) return 0;
  //   // Clamp percent between 0 and 100
  //   const percent = Math.round((currentStageIndex / totalStages) * 100);
  //   return Math.max(0, Math.min(percent, 100));
  // })();

  // Optionally, estimate time remaining (not implemented, placeholder)
  // const progressTimeEstimate = undefined;

  return (
    <div className="qa-results-container">
      <div className="qa-results-page">
        {/* Header */}
        <div className="results-header">
          <button className="back-link" onClick={handleBack}>
            <Icon type="arrow-left" size="small" />
            <span>Back to check your QA</span>
          </button>
        </div>

        {/* Task Status Display */}
        <div className="page-title">
          {taskResults ? (
            <div className="page-title-wrapper">
              <Headline3 className="results-title">QA Results</Headline3>
              <ButtonRound
                className="goto-dashboard-btn"
                children="Go to Dashboard"
                onClick={handleGoToDashboard}
              />
            </div>
          ) : taskState.error ? (
            <>
              <Headline3>Analysis Failed</Headline3>
              <Subtitle2 className="sub-title">
                {taskState.error || "An error occurred during analysis"}
              </Subtitle2>
            </>
          ) : (
            <div className="page-title-wrapper">
              <Headline3 className="results-title">QA Results</Headline3>
              <ButtonRound
                className="goto-dashboard-btn"
                children="Go to Dashboard"
                disabled
                onClick={handleGoToDashboard}
              />
            </div>
          )}
        </div>

        {/* Results Display */}
        {(taskResults || isPolling || isPollingResults) && (
          <div className="results-container">
            {/* Task Summary Section */}
            <div className="task-summary-section">
              {!taskResults ? (
                <>
                  {/* Skeleton Loaders for entire section */}
                  {/* Top Summary Cards Skeleton */}
                  <div className="summary-top-cards">
                    {/* Quality Score Card Skeleton */}
                    <div className="summary-card-large skeleton">
                      <div className="skeleton-label"></div>
                      <div
                        className="skeleton-value"
                        style={{ marginTop: "12px" }}
                      ></div>
                      <div
                        className="skeleton-text"
                        style={{
                          marginTop: "16px",
                          width: "70%",
                          height: "8px",
                        }}
                      ></div>
                      <div
                        className="skeleton-label"
                        style={{ marginTop: "12px" }}
                      ></div>
                    </div>
                    {/* Total Checks Card Skeleton */}
                    <div className="summary-card-large skeleton">
                      <div
                        className="skeleton-icon"
                        style={{ marginBottom: "12px" }}
                      ></div>
                      <div className="skeleton-value"></div>
                      <div className="skeleton-label"></div>
                    </div>
                    {/* Checks Passed Card Skeleton */}
                    <div className="summary-card-large skeleton">
                      <div
                        className="skeleton-icon"
                        style={{ marginBottom: "12px" }}
                      ></div>
                      <div className="skeleton-value"></div>
                      <div className="skeleton-label"></div>
                    </div>
                    {/* Observations Card Skeleton */}
                    <div className="summary-card-large skeleton">
                      <div
                        className="skeleton-icon"
                        style={{ marginBottom: "12px" }}
                      ></div>
                      <div className="skeleton-value"></div>
                      <div className="skeleton-label"></div>
                    </div>
                    {/* Failed Checks Card Skeleton */}
                    <div className="summary-card-large skeleton">
                      <div
                        className="skeleton-icon"
                        style={{ marginBottom: "12px" }}
                      ></div>
                      <div className="skeleton-value"></div>
                      <div className="skeleton-label"></div>
                    </div>
                  </div>

                  {/* Task Details Section Skeleton */}
                  <div className="task-details-section">
                    <div
                      className="skeleton-label"
                      style={{ marginBottom: "24px", width: "200px" }}
                    ></div>

                    {/* Task Details Cards Skeleton */}
                    <div className="task-details-cards">
                      <div className="task-detail-card skeleton">
                        <div
                          className="skeleton-icon"
                          style={{ marginRight: "12px" }}
                        ></div>
                        <div>
                          <div
                            className="skeleton-label"
                            style={{ marginBottom: "8px" }}
                          ></div>
                          <div
                            className="skeleton-text"
                            style={{ width: "150px" }}
                          ></div>
                        </div>
                      </div>
                      <div className="task-detail-card skeleton">
                        <div
                          className="skeleton-icon"
                          style={{ marginRight: "12px" }}
                        ></div>
                        <div>
                          <div
                            className="skeleton-label"
                            style={{ marginBottom: "8px" }}
                          ></div>
                          <div
                            className="skeleton-text"
                            style={{ width: "150px" }}
                          ></div>
                        </div>
                      </div>
                      <div className="task-detail-card skeleton">
                        <div
                          className="skeleton-icon"
                          style={{ marginRight: "12px" }}
                        ></div>
                        <div>
                          <div
                            className="skeleton-label"
                            style={{ marginBottom: "8px" }}
                          ></div>
                          <div
                            className="skeleton-text"
                            style={{ width: "150px" }}
                          ></div>
                        </div>
                      </div>
                      <div className="task-detail-card skeleton">
                        <div
                          className="skeleton-icon"
                          style={{ marginRight: "12px" }}
                        ></div>
                        <div>
                          <div
                            className="skeleton-label"
                            style={{ marginBottom: "8px" }}
                          ></div>
                          <div
                            className="skeleton-text"
                            style={{ width: "150px" }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Files in this task Skeleton */}
                  <div className="files-in-task skeleton">
                    <div className="files-header">
                      <div
                        className="skeleton-text"
                        style={{ width: "200px" }}
                      ></div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Top Summary Cards */}
                  <div className="summary-top-cards">
                    {/* Overall Quality Score */}
                    <div className="summary-card-large quality-score">
                      <div className="card-label-large">
                        Overall Quality Score
                      </div>
                      {isResultsComplete ? (
                        <>
                          <div className="card-value-large">
                            {taskResults.outputs_json.compliance_score.toFixed(
                              1,
                            )}{" "}
                            <span className="out-of">/ 100</span>
                          </div>
                          {/* Progress Bar */}
                          <div
                            className="progress-bar-container"
                            style={{ marginTop: 16 }}
                          >
                            <div
                              className="progress-bar-bg"
                              style={{
                                width: "20rem",
                                height: 8,
                                background: "#ededed",
                                borderRadius: 4,
                                position: "relative",
                              }}
                            >
                              <div
                                className="progress-bar-fill"
                                style={{
                                  width: `${taskResults.outputs_json.compliance_score}%`,
                                  height: "100%",
                                  background: "#FFC433",
                                  borderRadius: 4,
                                  transition:
                                    "width 0.4s cubic-bezier(.4,1,.4,1)",
                                  position: "absolute",
                                  left: 0,
                                  top: 0,
                                }}
                              />
                            </div>
                          </div>
                          <div
                            className="quality-status"
                            title={
                              taskResults.outputs_json.compliance_score >= 90
                                ? "Excellent (90-100)"
                                : taskResults.outputs_json.compliance_score >=
                                    75
                                  ? "Needs Review (75-89)"
                                  : "Critical (0-74)"
                            }
                          >
                            {taskResults.outputs_json.compliance_score >= 90
                              ? "Excellent"
                              : taskResults.outputs_json.compliance_score >= 75
                                ? "Needs Review"
                                : "Critical"}
                          </div>
                        </>
                      ) : (
                        <>
                          <div
                            className="skeleton-value"
                            style={{ marginTop: "12px" }}
                          ></div>
                          <div
                            className="skeleton-text"
                            style={{
                              marginTop: "16px",
                              width: "20rem",
                              height: "8px",
                            }}
                          ></div>
                          <div
                            className="skeleton-label"
                            style={{
                              marginTop: "12px",
                              width: "10rem",
                              height: "1.5rem",
                            }}
                          ></div>
                        </>
                      )}
                    </div>

                    {/* Total Checks */}
                    <div className="summary-card-large">
                      <>
                        <div className="card-icon-header">
                          <span
                            role="img"
                            aria-label="total-checks"
                            style={{ height: "16px" }}
                          />
                        </div>
                        {isResultsComplete ? (
                          <div className="card-value-large">{totalChecks}</div>
                        ) : (
                          <div className="skeleton-value"></div>
                        )}
                      </>
                      <div className="card-label-large">Total Checks</div>
                    </div>

                    {/* Checks Passed */}
                    <div className="summary-card-large passed">
                      <>
                        <div className="card-icon-header">
                          <Icon
                            type="check-round"
                            size="small"
                            className="pass-icon"
                          />
                        </div>
                        {isResultsComplete ? (
                          <div className="card-value-large">
                            {passedFindings.length}
                          </div>
                        ) : (
                          <div className="skeleton-value"></div>
                        )}
                      </>
                      <div className="card-label-large">Checks Passed</div>
                    </div>

                    {/* Warning Checks */}
                    <div className="summary-card-large warnings">
                      <>
                        <div className="card-icon-header">
                          <WarningIcon className="warning-icon" />
                        </div>
                        {isResultsComplete ? (
                          <div className="card-value-large">
                            {otherFindings.length}
                          </div>
                        ) : (
                          <div className="skeleton-value"></div>
                        )}
                      </>
                      <div className="card-label-large">Observations</div>
                    </div>
                    {/* Other Checks */}
                    {/* <div className="summary-card-large other">
                      <div className="card-icon-header">
                        <Icon
                          type="close"
                          size="small"
                          className="other-icon"
                        />
                      </div>
                      <div className="card-value-large">
                        {otherFindings.length}
                      </div>
                      <div className="card-label-large">Other</div>
                    </div> */}
                    {/* Failed Checks */}
                    <div className="summary-card-large failed">
                      <>
                        <div className="card-icon-header">
                          <FailIcon className="status-icon" />
                        </div>
                        {isResultsComplete ? (
                          <div className="card-value-large">
                            {failedFindings.length}
                          </div>
                        ) : (
                          <div className="skeleton-value"></div>
                        )}
                      </>
                      <div className="card-label-large">Failed Checks</div>
                    </div>
                  </div>

                  <div className="task-details-section">
                    <Headline3>Task Details</Headline3>

                    {/* Task Details Cards */}
                    <div className="task-details-cards">
                      <div className="task-detail-card">
                        <TaskIdIcon className="task-detail-icon" />
                        <div className="task-detail-content">
                          <div className="task-detail-label">Task ID</div>
                          <div className="task-detail-value">
                            {taskResults.task_id}
                          </div>
                        </div>
                      </div>
                      <div className="task-detail-card">
                        <BrandIcon className="task-detail-icon" />
                        <div className="task-detail-content">
                          <div className="task-detail-label">Brand</div>
                          <div className="task-detail-value">
                            {taskResults.outputs_json.brand}
                          </div>
                        </div>
                      </div>
                      <div className="task-detail-card">
                        <ChannelIcon className="task-detail-icon" />
                        <div className="task-detail-content">
                          <div className="task-detail-label">Channel</div>
                          <div className="task-detail-value">
                            {formatChannelName(
                              taskResults.outputs_json.channel,
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="task-detail-card">
                        <DateIcon className="task-detail-icon" />
                        <div className="task-detail-content">
                          <div className="task-detail-label">Analysis Date</div>
                          <div className="task-detail-value">
                            {new Date(
                              taskResults.outputs_json.analysis_date,
                            ).toLocaleString("en-US", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Files in this task */}
                  {[
                    uploadedFiles.storyboard,
                    uploadedFiles.manuscript,
                    uploadedFiles.brief,
                    figmaDetails.url,
                  ].filter(Boolean).length > 0 &&
                    taskCreated === taskResults.task_id && (
                      <div className="files-in-task">
                        <div
                          className="files-header"
                          onClick={() => setFilesExpanded(!filesExpanded)}
                        >
                          <Icon
                            type={filesExpanded ? "chevron-up" : "chevron-down"}
                            size="small"
                          />
                          <span>
                            Files in this task (
                            {
                              [
                                uploadedFiles.storyboard,
                                uploadedFiles.manuscript,
                                uploadedFiles.brief,
                                figmaDetails.url,
                              ].filter(Boolean).length
                            }
                            )
                          </span>
                        </div>
                        {filesExpanded && (
                          <div className="files-list">
                            {uploadedFiles.storyboard && (
                              <div className="file-item">
                                <Icon type="pdf" size="small" />
                                <div className="file-details">
                                  <span className="file-name">
                                    {uploadedFiles.storyboard.name}
                                  </span>
                                  <span className="file-type">Storyboard</span>
                                </div>
                              </div>
                            )}
                            {uploadedFiles.manuscript && (
                              <div className="file-item">
                                <Icon type="word" size="small" />
                                <div className="file-details">
                                  <span className="file-name">
                                    {uploadedFiles.manuscript.name}
                                  </span>
                                  <span className="file-type">Manuscript</span>
                                </div>
                              </div>
                            )}
                            {uploadedFiles.brief && (
                              <div className="file-item">
                                <Icon type="excel" size="small" />
                                <div className="file-details">
                                  <span className="file-name">
                                    {uploadedFiles.brief.name}
                                  </span>
                                  <span className="file-type">Brief</span>
                                </div>
                              </div>
                            )}
                            {figmaDetails.url && (
                              <div className="file-item">
                                <Icon type="link" size="small" />
                                <div className="file-details">
                                  <span className="file-name">
                                    {figmaDetails.url}
                                  </span>
                                  <span className="file-type">Figma</span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                </>
              )}
            </div>

            {/* Compliance Checks Section - Using Categorized Findings */}
            {!taskResults ? (
              <div className="compliance-checks-section skeleton">
                <div className="section-header">
                  <div
                    className="skeleton-label"
                    style={{ width: "250px" }}
                  ></div>
                </div>

                {/* Storyboard Section Skeleton */}
                <div className="source-type-group">
                  <div className="source-type-header">
                    <div
                      className="skeleton-label"
                      style={{ width: "200px" }}
                    ></div>
                  </div>
                </div>

                {/* Manuscript Section Skeleton */}
                <div className="source-type-group">
                  <div className="source-type-header">
                    <div
                      className="skeleton-label"
                      style={{ width: "180px" }}
                    ></div>
                  </div>
                </div>

                {/* Figma Section Skeleton */}
                <div className="source-type-group">
                  <div className="source-type-header">
                    <div
                      className="skeleton-label"
                      style={{ width: "150px" }}
                    ></div>
                  </div>
                </div>

                {/* Finding Cards Skeleton */}
                <div style={{ marginTop: "24px" }}>
                  <div
                    className="skeleton-finding-card"
                    style={{ marginBottom: "16px" }}
                  >
                    <div
                      className="skeleton-label"
                      style={{ marginBottom: "8px" }}
                    ></div>
                    <div
                      className="skeleton-text"
                      style={{ width: "80%", marginBottom: "8px" }}
                    ></div>
                    <div
                      className="skeleton-text"
                      style={{ width: "60%" }}
                    ></div>
                  </div>
                  <div
                    className="skeleton-finding-card"
                    style={{ marginBottom: "16px" }}
                  >
                    <div
                      className="skeleton-label"
                      style={{ marginBottom: "8px" }}
                    ></div>
                    <div
                      className="skeleton-text"
                      style={{ width: "75%", marginBottom: "8px" }}
                    ></div>
                    <div
                      className="skeleton-text"
                      style={{ width: "65%" }}
                    ></div>
                  </div>
                  <div className="skeleton-finding-card">
                    <div
                      className="skeleton-label"
                      style={{ marginBottom: "8px" }}
                    ></div>
                    <div
                      className="skeleton-text"
                      style={{ width: "70%", marginBottom: "8px" }}
                    ></div>
                    <div
                      className="skeleton-text"
                      style={{ width: "55%" }}
                    ></div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="compliance-checks-section">
                <div className="section-header">
                  <Headline3>Compliance Checks</Headline3>
                </div>

                {/* {loadingImages && (
                  <div className="loading-images">
                    <Loader style="mini" />
                    <span>Loading frame images...</span>
                  </div>
                )} */}

                {/* Summary Cards */}
                {/* <div className="summary-cards">
                  <div
                    className={`summary-card ${activeFilter === "all" ? "active" : ""}`}
                    onClick={() => handleFilterChange("all")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="card-label">Total Checks</div>
                    <div className="card-value">{totalChecks}</div>
                  </div>
                  <div
                    className={`summary-card pass ${activeFilter === "passed" ? "active" : ""}`}
                    onClick={() => handleFilterChange("passed")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="card-label">Passed</div>
                    <div className="card-value">{passedFindings.length}</div>
                  </div>
                  <div
                    className={`summary-card fail ${activeFilter === "failed" ? "active" : ""}`}
                    onClick={() => handleFilterChange("failed")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="card-label">Failed</div>
                    <div className="card-value">{failedFindings.length}</div>
                  </div>
                </div> */}

                {/* Figma Section */}
                {(filteredFigmaFrameFindings.length > 0 ||
                  filteredFigmaFindings.length > 0) && (
                  <div className="source-type-group">
                    <div
                      className="source-type-header"
                      onClick={() => handleSectionToggle("figma")}
                    >
                      <Icon
                        type={
                          expandedSection === "figma"
                            ? "chevron-up"
                            : "chevron-down"
                        }
                        size="small"
                      />
                      <Headline3>Figma</Headline3>
                    </div>
                    {expandedSection === "figma" && (
                      <FigmaSection
                        figmaFrameFindings={filteredFigmaFrameFindings}
                        figmaFindings={filteredFigmaFindings}
                        channel={taskResults?.outputs_json.channel}
                        frameImages={frameImages}
                        expandedFrames={expandedFrames}
                        expandedFindings={expandedFindings}
                        setExpandedFrames={setExpandedFrames}
                        setExpandedFindings={setExpandedFindings}
                        isResultsComplete={isResultsComplete}
                      />
                    )}
                  </div>
                )}
                {/* Storyboard Section */}
                {(filteredFrameFindings.length > 0 ||
                  filteredStoryboardFindings.length > 0 ||
                  filteredOtherStoryboardFindings.length > 0) && (
                  <div className="source-type-group">
                    <div
                      className="source-type-header"
                      onClick={() => handleSectionToggle("storyboard")}
                    >
                      <Icon
                        type={
                          expandedSection === "storyboard"
                            ? "chevron-up"
                            : "chevron-down"
                        }
                        size="small"
                      />
                      <Headline3>Storyboard</Headline3>
                    </div>
                    {expandedSection === "storyboard" && (
                      <StoryboardSection
                        channel={taskResults.outputs_json.channel?.toLowerCase()}
                        frameFindings={filteredFrameFindings}
                        storyboardFindings={filteredStoryboardFindings}
                        otherStoryboardFindings={
                          filteredOtherStoryboardFindings
                        }
                        frameImages={frameImages}
                        frameSasUrls={frameSasUrls}
                        expandedFrames={expandedFrames}
                        expandedFindings={expandedFindings}
                        setExpandedFrames={setExpandedFrames}
                        setExpandedFindings={setExpandedFindings}
                        isResultsComplete={isResultsComplete}
                      />
                    )}
                  </div>
                )}

                {/* Manuscript Section */}
                {filteredManuscriptFindings.length > 0 && (
                  <div className="source-type-group">
                    <div
                      className="source-type-header"
                      onClick={() => handleSectionToggle("manuscript")}
                    >
                      <Icon
                        type={
                          expandedSection === "manuscript"
                            ? "chevron-up"
                            : "chevron-down"
                        }
                        size="small"
                      />
                      <Headline3>Manuscript</Headline3>
                    </div>
                    {expandedSection === "manuscript" && (
                      <ManuscriptSection
                        manuscriptFindings={filteredManuscriptFindings}
                        expandedFindings={expandedFindings}
                        expandedFrames={expandedFrames}
                        setExpandedFindings={setExpandedFindings}
                        setExpandedFrames={setExpandedFrames}
                        isResultsComplete={isResultsComplete}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Loading State */}
        {isPolling && !taskResults && !pollingError && (
          <div className="analysis-modal-overlay">
            <div className="analysis-modal">
              <div className="modal-header-wrapper">
                <div className="analysis-modal-header">
                  <Headline3>Running your analysis</Headline3>
                  <Subtitle2 className="modal-subtitle">
                    You'll be taken to your results when it's complete.
                  </Subtitle2>
                </div>

                <div className="analysis-modal-close">
                  {showTooltip && (
                    // <Tooltip label="Warning: Closing will cancel the analysis">
                    <div className="custom-tooltip">
                      Warning: Closing will cancel the analysis
                    </div>
                    // </Tooltip>
                  )}
                  <ButtonIcon
                    iconType="close"
                    label=""
                    className="close-btn"
                    onClick={handleClose}
                    onMouseEnter={() => {
                      if (!showTooltip) {
                        setShowTooltip(true);
                      }
                    }}
                    onMouseLeave={() => {
                      if (showTooltip) {
                        setShowTooltip(false);
                      }
                    }}
                  />
                </div>
              </div>

              {taskState.taskId && (
                <div className="task-id-display-modal">
                  <span className="task-id-label">Task ID:</span>
                  <span className="task-id-value">{taskState.taskId}</span>
                </div>
              )}

              <div className="analysis-progress">
                <div className="progress-stages">
                  {progressStages.map((stage, index) => {
                    const currentStageIndex = getStatusStage(currentStatus);
                    const isCompleted = index < currentStageIndex;
                    const isActive = index === currentStageIndex;

                    // Render appropriate icon based on stage
                    const renderIcon = () => {
                      if (isCompleted) {
                        return <CheckmarkIcon />;
                      }

                      // Set fill color for icons
                      const iconFill = isActive ? "white" : "black";
                      const iconFillOpacity = isActive ? "1" : "0.2";
                      // const iconProps = {
                      //   fill: iconFill,
                      //   fillOpacity: iconFillOpacity,
                      // };

                      switch (stage.key) {
                        case "new":
                          return (
                            <NewStateIcon
                              fill={iconFill}
                              fillOpacity={iconFillOpacity}
                            />
                          );
                        case "submitted":
                          return (
                            <SubmittedIcon
                              fill={iconFill}
                              fillOpacity={iconFillOpacity}
                            />
                          );
                        case "routed":
                          return (
                            <RoutedIcon
                              fill={iconFill}
                              fillOpacity={iconFillOpacity}
                            />
                          );
                        case "planned":
                          return (
                            <PlanningIcon
                              fill={iconFill}
                              fillOpacity={iconFillOpacity}
                            />
                          );
                        case "running":
                          return (
                            <RunningIcon
                              fill={iconFill}
                              fillOpacity={iconFillOpacity}
                            />
                          );
                        case "complete":
                          return (
                            <CompleteIcon
                              fill={iconFill}
                              fillOpacity={iconFillOpacity}
                            />
                          );
                        default:
                          return (
                            <span className="stage-number">{index + 1}</span>
                          );
                      }
                    };

                    return (
                      <div key={stage.key} className="progress-stage">
                        <div
                          className={`stage-circle ${
                            isCompleted
                              ? "completed"
                              : isActive
                                ? "active"
                                : "pending"
                          }`}
                        >
                          {renderIcon()}
                        </div>
                        {index < progressStages.length - 1 && (
                          <div
                            className={`stage-connector ${isCompleted ? "completed" : ""}`}
                          />
                        )}
                        <div className="stage-label">{stage.label}</div>
                      </div>
                    );
                  })}
                </div>

                {/* <div
                  className="progress-bar-container"
                  style={{ marginTop: 24, marginBottom: 8 }}
                >
                  <div
                    className="progress-bar-bg"
                    style={{
                      width: "100%",
                      height: 8,
                      background: "#ededed",
                      borderRadius: 4,
                      position: "relative",
                    }}
                  >
                    <div
                      className="progress-bar-fill"
                      style={{
                        width: `${progressPercent || 0}%`,
                        height: "100%",
                        background: "#be2bbb",
                        borderRadius: 4,
                        transition: "width 0.4s cubic-bezier(.4,1,.4,1)",
                        position: "absolute",
                        left: 0,
                        top: 0,
                      }}
                    />
                  </div>
                  <div
                    className="progress-bar-label"
                    style={{
                      textAlign: "center",
                      marginTop: 8,
                      color: "#6c6c6c",
                      fontWeight: 500,
                    }}
                  >
                    {progressPercent ? `${progressPercent}%` : "0%"}
                    {progressTimeEstimate ? ` / ${progressTimeEstimate}` : ""}
                  </div>
                </div> */}
              </div>
            </div>
          </div>
        )}

        {/* Error State with Retry */}
        {pollingError && !taskResults && (
          <div className="analysis-modal-overlay">
            <div className="analysis-modal">
              <div className="modal-header-wrapper">
                <div className="analysis-modal-header">
                  <Headline3>Running your analysis</Headline3>
                  <Subtitle2 className="modal-subtitle">
                    You'll be taken to your results when it's complete.
                  </Subtitle2>
                </div>

                <div className="analysis-modal-close">
                  {showTooltip && (
                    <div className="custom-tooltip">
                      Warning: Closing will cancel the analysis
                    </div>
                  )}
                  <ButtonIcon
                    iconType="close"
                    label=""
                    className="close-btn"
                    onClick={handleClose}
                    onMouseEnter={() => {
                      if (!showTooltip) {
                        setShowTooltip(true);
                      }
                    }}
                    onMouseLeave={() => {
                      if (showTooltip) {
                        setShowTooltip(false);
                      }
                    }}
                  />
                </div>
              </div>

              {taskState.taskId && (
                <div className="task-id-display-modal">
                  <span className="task-id-label">Task ID:</span>
                  <span className="task-id-value">{taskState.taskId}</span>
                </div>
              )}

              <div className="analysis-progress">
                <div className="progress-stages">
                  {progressStages.map((stage, index) => {
                    // All stages are inactive in error state
                    const renderIcon = () => {
                      // All icons with black fill and 0.2 opacity (inactive)
                      const iconFill = "black";
                      const iconFillOpacity = "0.2";

                      switch (stage.key) {
                        case "new":
                          return (
                            <NewStateIcon
                              fill={iconFill}
                              fillOpacity={iconFillOpacity}
                            />
                          );
                        case "submitted":
                          return (
                            <SubmittedIcon
                              fill={iconFill}
                              fillOpacity={iconFillOpacity}
                            />
                          );
                        case "routed":
                          return (
                            <RoutedIcon
                              fill={iconFill}
                              fillOpacity={iconFillOpacity}
                            />
                          );
                        case "planned":
                          return (
                            <PlanningIcon
                              fill={iconFill}
                              fillOpacity={iconFillOpacity}
                            />
                          );
                        case "running":
                          return (
                            <RunningIcon
                              fill={iconFill}
                              fillOpacity={iconFillOpacity}
                            />
                          );
                        case "complete":
                          return (
                            <CompleteIcon
                              fill={iconFill}
                              fillOpacity={iconFillOpacity}
                            />
                          );
                        default:
                          return (
                            <span className="stage-number">{index + 1}</span>
                          );
                      }
                    };

                    return (
                      <div key={stage.key} className="progress-stage">
                        <div className="stage-circle pending">
                          {renderIcon()}
                        </div>
                        {index < progressStages.length - 1 && (
                          <div className="stage-connector" />
                        )}
                        <div className="stage-label">{stage.label}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Error Message instead of Progress Bar */}
                <div className="error-message-container">
                  <ErrorIcon className="error-icon" />
                  <span className="error-text">
                    Error - {pollingError}{" "}
                    <button className="retry-btn" onClick={handleRetry}>
                      Retry
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8.00033 4.00016V1.3335L4.66699 4.66683L8.00033 8.00016V5.3335C10.207 5.3335 12.0003 7.12683 12.0003 9.3335C12.0003 11.5402 10.207 13.3335 8.00033 13.3335C5.79366 13.3335 4.00033 11.5402 4.00033 9.3335H2.66699C2.66699 12.2802 5.05366 14.6668 8.00033 14.6668C10.947 14.6668 13.3337 12.2802 13.3337 9.3335C13.3337 6.38683 10.947 4.00016 8.00033 4.00016Z"
                          fill="#961515"
                        />
                      </svg>
                    </button>
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {taskResults && (
        <div className="qa-results-footer">
          <button
            className="download-btn"
            onClick={handleDownloadPDF}
            disabled={!isResultsComplete || downloadingPDF || downloadingCSV}
          >
            <span>{downloadingPDF ? "Downloading..." : "Download PDF"}</span>
            <Icon type="download" size="small" />
          </button>
          <button
            className="download-btn"
            onClick={handleDownloadCSV}
            disabled={!isResultsComplete || downloadingPDF || downloadingCSV}
          >
            <span>{downloadingCSV ? "Downloading..." : "Download CSV"}</span>
            <Icon type="download" size="small" />
          </button>
        </div>
      )}
    </div>
  );
};

export default QAResultsNew;
